import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { sendDeliveryAcceptedToUser, sendDeliveryRejectedToUser, sendDeliveryCompletedToUser } from '../services/whatsapp.service';

const STATUS_LABEL = {
  pending: { label: '⏳ Gözlənilir', color: '#f59e0b' },
  accepted: { label: '✅ Qəbul edildi', color: '#3b82f6' },
  courier_assigned: { label: '🚴 Kuryer təyin olundu', color: '#8b5cf6' },
  picked_up: { label: '📦 Götürüldü', color: '#0ea5e9' },
  delivered: { label: '✔️ Çatdırıldı', color: '#10b981' },
  rejected: { label: '❌ Rədd edildi', color: '#ef4444' },
  cancelled: { label: '🚫 Ləğv edildi', color: '#9ca3af' },
};

export default function DeliveryOrdersPage() {
  const { business } = useAuth();
  const { activeLocation } = useLocation();
  const [orders, setOrders] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const loadOrders = useCallback(async () => {
    if (!activeLocation?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from('delivery_orders')
      .select('*')
      .eq('business_location_id', activeLocation.id)
      .order('created_at', { ascending: false })
      .limit(50);
    setOrders(data || []);
    setLoading(false);
  }, [activeLocation?.id]);

  const loadCouriers = useCallback(async () => {
    if (!business?.id) return;
    const { data } = await supabase
      .from('couriers')
      .select('id, full_name, status, vehicle_type')
      .eq('business_id', business.id)
      .eq('type', 'cafe_employed')
      .eq('is_active', true);
    setCouriers(data || []);
  }, [business?.id]);

  useEffect(() => { loadOrders(); loadCouriers(); }, [loadOrders, loadCouriers]);

  useEffect(() => {
    if (!activeLocation?.id) return;
    const channel = supabase
      .channel(`delivery_orders_${activeLocation.id}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'delivery_orders',
        filter: `business_location_id=eq.${activeLocation.id}`,
      }, () => loadOrders())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [activeLocation?.id, loadOrders]);

  const runAction = async (id, fn, onSuccess) => {
    setBusyId(id); setError('');
    try {
      const { data, error: rpcErr } = await fn();
      if (rpcErr) throw new Error(rpcErr.message);
      if (!data?.success) throw new Error(data?.error || 'Xəta baş verdi');
      if (onSuccess) await onSuccess();
      loadOrders();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const getCustomerPhone = async (orderId) => {
    const { data } = await supabase.rpc('get_delivery_customer_phone', { p_order_id: orderId });
    return data?.success ? data.phone : null;
  };

  const handleAccept = (orderId) => runAction(orderId, () =>
    supabase.rpc('accept_delivery_order', { p_order_id: orderId }),
    async () => {
      const phone = await getCustomerPhone(orderId);
      if (phone) sendDeliveryAcceptedToUser(phone, business?.name);
    });

  const handleReject = (orderId) => {
    const reason = window.prompt('Rədd səbəbi:', 'Çox məşğuluq');
    if (reason === null) return;
    runAction(orderId, () => supabase.rpc('reject_delivery_order', {
      p_order_id: orderId, p_reason: reason, p_idempotency_key: crypto.randomUUID(),
    }), async () => {
      const phone = await getCustomerPhone(orderId);
      if (phone) sendDeliveryRejectedToUser(phone, business?.name, reason);
    });
  };

  const handleAssign = (orderId, courierId) => {
    if (!courierId) return;
    runAction(orderId, () => supabase.rpc('assign_courier', { p_order_id: orderId, p_courier_id: courierId }));
  };

  const handlePickedUp = (orderId) => runAction(orderId, () =>
    supabase.rpc('mark_picked_up', { p_order_id: orderId }));

  const handleDelivered = (orderId) => runAction(orderId, () =>
    supabase.rpc('complete_delivery', { p_order_id: orderId }),
    async () => {
      const phone = await getCustomerPhone(orderId);
      if (phone) sendDeliveryCompletedToUser(phone, business?.name);
    });

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>🚴 Çatdırılma Sifarişləri</h1>
      <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
        {activeLocation?.name || 'Lokasiya seçilməyib'}
      </p>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      {loading ? (
        <p>Yüklənir...</p>
      ) : orders.length === 0 ? (
        <p style={{ color: '#999' }}>Hələ çatdırılma sifarişi yoxdur.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {orders.map(o => {
            const st = STATUS_LABEL[o.status] || { label: o.status, color: '#888' };
            const busy = busyId === o.id;
            return (
              <div key={o.id} style={{ border: '2px solid #eee', borderRadius: 14, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>
                      🕐 {new Date(o.created_at).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}
                      {' · '}{o.delivery_address}
                    </div>
                    <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
                      {(o.items || []).map(it => `${it.quantity}× ${it.name}`).join(', ')}
                    </div>
                    <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                      Cəmi: {(o.subtotal + o.delivery_fee).toFixed(2)} ₼ (çatdırılma {o.delivery_fee.toFixed(2)} ₼)
                      {' · '}{o.payment_method === 'corporate_wallet' ? '💳 Korporativ' : '💵 Nağd'}
                    </div>
                  </div>
                  <span style={{ fontWeight: 700, fontSize: 12, color: st.color }}>{st.label}</span>
                </div>

                {o.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                    <button disabled={busy} onClick={() => handleAccept(o.id)} style={{
                      padding: '8px 16px', borderRadius: 8, border: 'none', background: '#10b981',
                      color: '#fff', fontWeight: 700, cursor: 'pointer',
                    }}>Qəbul Et</button>
                    <button disabled={busy} onClick={() => handleReject(o.id)} style={{
                      padding: '8px 16px', borderRadius: 8, border: '2px solid #fecaca', background: '#fef2f2',
                      color: '#dc2626', fontWeight: 700, cursor: 'pointer',
                    }}>Rədd Et</button>
                  </div>
                )}

                {o.status === 'accepted' && (
                  <div style={{ marginTop: 12 }}>
                    <select disabled={busy} defaultValue="" onChange={e => handleAssign(o.id, e.target.value)}
                      style={{ padding: '8px 12px', borderRadius: 8, border: '2px solid #ddd' }}>
                      <option value="" disabled>Kuryer seç...</option>
                      {couriers.map(c => (
                        <option key={c.id} value={c.id} disabled={c.status !== 'available'}>
                          {c.full_name} {c.status !== 'available' ? '(məşğul)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {o.status === 'courier_assigned' && (
                  <div style={{ marginTop: 12 }}>
                    <button disabled={busy} onClick={() => handlePickedUp(o.id)} style={{
                      padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0ea5e9',
                      color: '#fff', fontWeight: 700, cursor: 'pointer',
                    }}>Kuryer Götürdü</button>
                  </div>
                )}

                {o.status === 'picked_up' && (
                  <div style={{ marginTop: 12 }}>
                    <button disabled={busy} onClick={() => handleDelivered(o.id)} style={{
                      padding: '8px 16px', borderRadius: 8, border: 'none', background: '#10b981',
                      color: '#fff', fontWeight: 700, cursor: 'pointer',
                    }}>Çatdırıldı</button>
                  </div>
                )}

                {o.status === 'rejected' && o.rejection_reason && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>Səbəb: {o.rejection_reason}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
